import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// FAQ page — fully content-driven by the faq.json namespace
// (src/i18n/locales/{en,zh}/faq.json). The page reads the entire
// sections array via t() with returnObjects, so adding/editing a
// question is just a JSON edit in both locale files.

interface FaqRelatedLink {
  label: string
  href: string
}

interface FaqItem {
  q: string
  a: string
  related?: FaqRelatedLink[]
}

interface FaqSection {
  id: string
  title: string
  intro?: string
  items: FaqItem[]
}

const STAMP_CLASSES = ['n1', 'n2', 'n3'] as const

function filterSection(items: FaqItem[], q: string): FaqItem[] {
  if (!q.trim()) return items
  const lower = q.toLowerCase()
  return items.filter(
    (it) => it.q.toLowerCase().includes(lower) || it.a.toLowerCase().includes(lower),
  )
}

export default function Faq() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('faq')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })
  const [query, setQuery] = useState('')

  // Pull the entire structured sections array from the namespace.
  const rawSections = t('sections', { returnObjects: true }) as FaqSection[]

  const filtered = useMemo(
    () =>
      rawSections
        .map((s, i) => ({
          ...s,
          items: filterSection(s.items, query),
          stamp: STAMP_CLASSES[i % STAMP_CLASSES.length],
        }))
        .filter((s) => s.items.length > 0),
    [query, rawSections],
  )

  const totalMatches = filtered.reduce((sum, s) => sum + s.items.length, 0)

  return (
    <div className="faq">
      <section className="faq-hero">
        <div className="faq-hero-inner">
          <div className="breadcrumb">
            <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>
              {tCommon('nav.home')}
            </Link>{' '}
            / <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <div className="eyebrow" style={{ marginTop: 16 }}>{t('hero.eyebrow')}</div>
          <h1 className="faq-hero-h1">
            {t('hero.titlePart1')}<em>{t('hero.titleEmphasis')}</em>
          </h1>
          <p className="lede faq-hero-lede">{t('hero.lede')}</p>

          <div className="faq-search">
            <Icon name="search" size={16} />
            <input
              type="text"
              placeholder={t('hero.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t('hero.searchPlaceholder')}
            />
            {query && (
              <button
                type="button"
                className="faq-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <Icon name="x" size={12} />
              </button>
            )}
          </div>

          {!query && (
            <nav className="faq-toc">
              {rawSections.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="faq-toc-item">
                  {s.title}
                </a>
              ))}
            </nav>
          )}
        </div>
      </section>

      {query && (
        <section className="section" style={{ background: 'var(--bg)', padding: '24px 32px 0' }}>
          <div className="section-inner" style={{ maxWidth: 980 }}>
            <div className="faq-search-meta">
              {totalMatches === 0
                ? t('search.noResults', { query })
                : totalMatches === 1
                  ? t('search.resultCountOne', { count: totalMatches, query })
                  : t('search.resultCountOther', { count: totalMatches, query })}
            </div>
          </div>
        </section>
      )}

      {filtered.map((section, idx) => (
        <section
          key={section.id}
          id={section.id}
          className="section how-deep faq-deep"
          style={{ background: idx % 2 === 0 ? 'var(--bg-sunk)' : 'var(--bg)' }}
        >
          <div className="section-inner">
            <div className="how-deep-head">
              <div className={`how-deep-stamp ${section.stamp}`}>0{idx + 1}</div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 8 }}>{t('sectionLabel', { n: idx + 1 })}</div>
                <h2 className="h1">{section.title}</h2>
                {section.intro && (
                  <p className="lede faq-section-lede">{section.intro}</p>
                )}
              </div>
            </div>

            <div className="faq-list">
              {section.items.map((it, i) => (
                <details key={it.q} className="faq-row" open={i === 0 && !query}>
                  <summary>
                    <span className="faq-q">{it.q}</span>
                    <span className="faq-chev" aria-hidden="true">
                      <Icon name="chevron" size={14} />
                    </span>
                  </summary>
                  <div className="faq-a">
                    <p>{it.a}</p>
                    {it.related && it.related.length > 0 && (
                      <div className="faq-related">
                        <span className="faq-related-label">{t('relatedLabel')}</span>
                        <div className="faq-related-chips">
                          {it.related.map((r) => {
                            const external = !r.href.startsWith('/')
                            const localized = external ? r.href : withLang(r.href, lang)
                            return external ? (
                              <a key={r.href} href={localized} className="faq-related-chip">
                                {r.label}
                                <Icon name="arrow" size={12} />
                              </a>
                            ) : (
                              <Link key={r.href} to={localized} className="faq-related-chip">
                                {r.label}
                                <Icon name="arrow" size={12} />
                              </Link>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="section how-cta">
        <div className="section-inner">
          <div className="how-cta-card faq-cta-card">
            <div className="how-cta-body" style={{ padding: '56px' }}>
              <div className="eyebrow how-cta-eyebrow">{t('cta.eyebrow')}</div>
              <h2 className="h1 how-cta-h1">
                {t('cta.titlePart1')}<em>{t('cta.titleEmphasis')}</em>
              </h2>
              <p className="lede how-cta-lede">{t('cta.lede')}</p>
              <div className="how-cta-row">
                <a href="mailto:support@nimvoy.com" className="btn primary lg">
                  {t('cta.primary')} <Icon name="arrow" size={14} />
                </a>
                <button
                  type="button"
                  className="btn ghost lg"
                  onClick={() => navigate(withLang('/how-it-works', lang))}
                >
                  {t('cta.ghost')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO: contextual internal link to the blog article (English only). */}
      {lang === 'en' && (
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-3)', margin: '0 auto', padding: '8px 24px 8px', maxWidth: 640 }}>
          Want the deeper story?{' '}
          <a href="https://blog.nimvoy.com/blog/unpack-travel-esim-business/" style={{ color: '#B23A20' }}>
            How to tell a good travel eSIM from a cheap one →
          </a>
        </p>
      )}
    </div>
  )
}
